import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  EyeIcon,
  PencilIcon,
  TrashIcon,
  DocumentTextIcon,
  CalendarIcon,
  UserIcon,
  ChartBarIcon,
  ArrowDownTrayIcon
} from "@heroicons/react/24/outline";
import { toast } from 'react-toastify';
import {
  fetchProgressSummaries,
  deleteProgressSummary,
  setCurrentProgressSummary
} from "../../store/thunks/progressSummaryThunk";
import ProgressSummaryForm from "./ProgressSummaryForm";
import ConfirmDialog from "./ConfirmDialog";
import SearchDropdown from "./SearchDropdown";
import { downloadProgressSummaryPDF } from "../../utils/progressSummaryPdfGenerator";

const ProgressSummaryList = ({ onClose }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const {
    progressSummaries,
    pagination,
    loading,
    deleteLoading,
    error
  } = useSelector((state) => state.progressSummary);

  const [selectedSummary, setSelectedSummary] = useState(null);
  const [editingSummary, setEditingSummary] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedClient, setSelectedClient] = useState(null);

  // Load progress summaries on component mount
  useEffect(() => {
    console.log(`[Progress Summary List] Loading progress summaries`);
    const params = { page: currentPage, limit: 10 };
    if (selectedClient?._id) {
      params.clientId = selectedClient._id;
    }
    dispatch(fetchProgressSummaries(params));
  }, [dispatch, currentPage, selectedClient]);

  const handleView = (summary) => {
    console.log(`[Progress Summary List] Viewing summary:`, summary._id);
    setSelectedSummary(summary);
    setShowViewModal(true);
  };

  const handleEdit = (summary) => {
    console.log(`[Progress Summary List] Editing summary:`, summary._id);
    setEditingSummary(summary);
    setShowEditForm(true);
  };

  const handleDelete = (summary) => {
    console.log(`[Progress Summary List] Confirming delete for summary:`, summary._id);
    setConfirmDelete(summary);
  };

  const handleDownloadPDF = async (summary) => {
    try {
      console.log(`[Progress Summary List] Downloading PDF for:`, summary.clientName);
      await downloadProgressSummaryPDF(summary);
      toast.success("PDF downloaded successfully");
    } catch (error) {
      console.error(`[Progress Summary List] Error downloading PDF:`, error);
      toast.error("Failed to download PDF");
    }
  };

  const confirmDeleteSummary = async () => {
    if (!confirmDelete) return;

    try {
      console.log(`[Progress Summary List] Deleting summary:`, confirmDelete._id);
      await dispatch(deleteProgressSummary(confirmDelete._id)).unwrap();
      toast.success("Progress summary deleted successfully");
      setConfirmDelete(null);

      // Refresh the list
      dispatch(fetchProgressSummaries({ page: currentPage, limit: 10 }));
    } catch (error) {
      console.error(`[Progress Summary List] Error deleting summary:`, error);
      toast.error("Failed to delete progress summary");
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleClientSelection = (client) => {
    console.log(`[Progress Summary List] Client selected for filtering:`, client);
    setSelectedClient(client);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleClearFilter = () => {
    console.log(`[Progress Summary List] Clearing client filter`);
    setSelectedClient(null);
    setCurrentPage(1);
  };

  const handleEditFormSave = () => {
    // Refresh the list after saving
    dispatch(fetchProgressSummaries({ page: currentPage, limit: 10 }));
    setShowEditForm(false);
    setEditingSummary(null);
  };

  const calculateProgress = (goals) => {
    if (!goals || goals.length === 0) return 0;

    const totalGoals = goals.length;
    const achievedGoals = goals.filter(goal => goal.achieved).length;
    const emergingGoals = goals.filter(goal =>
      goal.emergingMaximum || goal.emergingModerate || goal.emergingMinimal
    ).length;

    // Weight achieved goals as 100%, emerging goals as 50%
    const weightedProgress = (achievedGoals * 1.0) + (emergingGoals * 0.5);
    return Math.round((weightedProgress / totalGoals) * 100);
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return "text-green-600 bg-green-100";
    if (percentage >= 60) return "text-yellow-600 bg-yellow-100";
    if (percentage >= 40) return "text-orange-600 bg-orange-100";
    return "text-red-600 bg-red-100";
  };

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-70">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 p-6">
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            <span className="ml-2 text-gray-600">Loading progress summaries...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-70">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 p-6">
          <div className="text-center">
            <div className="text-red-600 mb-4">Error: {error}</div>
            <button
              onClick={() => dispatch(fetchProgressSummaries({ page: currentPage, limit: 10 }))}
              className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700">
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="fixed inset-0 flex items-center justify-center z-70 overflow-y-auto">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl max-w-7xl w-full mx-4 max-h-[95vh] overflow-y-auto">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                Progress Summary Reports
              </h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Search and Filter */}
            <div className="flex items-center space-x-3">
              <div className="flex-1">
                <SearchDropdown
                  displayFields={["firstName", "lastName"]}
                  onSelection={handleClientSelection}
                  prefix=":client:"
                  placeholder="Search by client name to filter..."
                  initialSelection={selectedClient}
                  separator=" "
                />
              </div>
              {selectedClient && (
                <button
                  onClick={handleClearFilter}
                  className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-sm hover:bg-gray-200 transition-colors">
                  Clear Filter
                </button>
              )}
            </div>

            {/* Active Filter Display */}
            {selectedClient && (
              <div className="mt-3 flex items-center text-sm text-gray-600">
                <span className="font-medium">Filtered by:</span>
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                  {selectedClient.firstName} {selectedClient.lastName}
                </span>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="p-6">
            {progressSummaries.length === 0 ? (
              <div className="text-center py-12">
                <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No progress summaries</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Get started by creating your first progress summary report.
                </p>
              </div>
            ) : (
              <>
                {/* Progress Summaries Grid */}
                <div className="grid grid-cols-1 gap-4">
                  {progressSummaries.map((summary) => {
                    const progressPercentage = calculateProgress(summary.goals);
                    const progressColorClass = getProgressColor(progressPercentage);

                    return (
                      <div
                        key={summary._id}
                        className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            {/* Header Info */}
                            <div className="flex items-center space-x-4 mb-3">
                              <div className="flex items-center">
                                <UserIcon className="h-5 w-5 text-gray-400 mr-2" />
                                <span className="font-medium text-gray-900">
                                  {summary.clientName}
                                </span>
                                {summary.clientId && (
                                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                    Linked Client
                                  </span>
                                )}
                              </div>

                              <div className="flex items-center">
                                <CalendarIcon className="h-5 w-5 text-gray-400 mr-2" />
                                <span className="text-sm text-gray-600">
                                  {new Date(summary.date).toLocaleDateString()}
                                </span>
                              </div>

                              <div className="flex items-center">
                                <ChartBarIcon className="h-5 w-5 text-gray-400 mr-2" />
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${progressColorClass}`}>
                                  {progressPercentage}% Progress
                                </span>
                              </div>
                            </div>

                            {/* Summary Details */}
                            <div className="grid grid-cols-2 gap-4 mb-4">
                              <div>
                                <div className="text-sm text-gray-500">Session Number</div>
                                <div className="text-sm font-medium">{summary.sessionNumber || 'N/A'}</div>
                              </div>
                              <div>
                                <div className="text-sm text-gray-500">Goals</div>
                                <div className="text-sm font-medium">
                                  {summary.goals?.length || 0} goal(s)
                                </div>
                              </div>
                              <div>
                                <div className="text-sm text-gray-500">Clinician(s)</div>
                                <div className="text-sm font-medium">{summary.clinicians || 'N/A'}</div>
                              </div>
                              <div>
                                <div className="text-sm text-gray-500">Created By</div>
                                <div className="text-sm font-medium">
                                  {summary.createdBy?.firstName} {summary.createdBy?.lastName}
                                </div>
                              </div>
                            </div>

                            {/* Comments Preview */}
                            {summary.comments && (
                              <div className="mb-4">
                                <div className="text-sm text-gray-500 mb-1">Comments</div>
                                <div className="text-sm text-gray-700 line-clamp-2">
                                  {summary.comments.substring(0, 100)}
                                  {summary.comments.length > 100 && '...'}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Action Buttons */}
                          <div className="flex items-center space-x-2 ml-4">
                            <button
                              onClick={() => handleView(summary)}
                              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
                              title="View Details">
                              <EyeIcon className="h-5 w-5" />
                            </button>

                            <button
                              onClick={() => handleDownloadPDF(summary)}
                              className="p-2 text-indigo-400 hover:text-indigo-600 hover:bg-indigo-100 rounded-full"
                              title="Download PDF">
                              <ArrowDownTrayIcon className="h-5 w-5" />
                            </button>

                            {/* Only allow editing if user created it or is admin */}
                            {(summary.createdBy?._id === user._id || user.role?.includes('admin')) && (
                              <button
                                onClick={() => handleEdit(summary)}
                                className="p-2 text-blue-400 hover:text-blue-600 hover:bg-blue-100 rounded-full"
                                title="Edit">
                                <PencilIcon className="h-5 w-5" />
                              </button>
                            )}

                            {/* Only allow deletion if user created it or is admin */}
                            {(summary.createdBy?._id === user._id || user.role?.includes('admin')) && (
                              <button
                                onClick={() => handleDelete(summary)}
                                className="p-2 text-red-400 hover:text-red-600 hover:bg-red-100 rounded-full"
                                title="Delete">
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <div className="mt-6 flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                      {Math.min(pagination.page * pagination.limit, pagination.totalDocs)} of{' '}
                      {pagination.totalDocs} results
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={!pagination.hasPrevPage}
                        className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                        Previous
                      </button>
                      <span className="px-3 py-2 text-sm bg-indigo-50 border border-indigo-200 rounded-md">
                        Page {pagination.page} of {pagination.totalPages}
                      </span>
                      <button
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={!pagination.hasNextPage}
                        className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* View Modal */}
      {showViewModal && selectedSummary && (
        <ProgressSummaryViewModal
          summary={selectedSummary}
          onClose={() => {
            setShowViewModal(false);
            setSelectedSummary(null);
          }}
        />
      )}

      {/* Edit Form Modal */}
      {showEditForm && editingSummary && (
        <ProgressSummaryForm
          editingProgressSummary={editingSummary}
          onClose={() => {
            setShowEditForm(false);
            setEditingSummary(null);
          }}
          onSave={handleEditFormSave}
        />
      )}

      {/* Confirm Delete Dialog */}
      {confirmDelete && (
        <ConfirmDialog
          isOpen={true}
          title="Delete Progress Summary"
          message={`Are you sure you want to delete the progress summary for ${confirmDelete.clientName}? This action cannot be undone.`}
          confirmText="Delete"
          type="danger"
          onConfirm={confirmDeleteSummary}
          onClose={() => setConfirmDelete(null)}
        />
      )}
    </>
  );
};

// View Modal Component
const ProgressSummaryViewModal = ({ summary, onClose }) => {
  const handleDownloadPDF = async () => {
    try {
      console.log(`[Progress Summary View Modal] Downloading PDF for:`, summary.clientName);
      await downloadProgressSummaryPDF(summary);
      toast.success("PDF downloaded successfully");
    } catch (error) {
      console.error(`[Progress Summary View Modal] Error downloading PDF:`, error);
      toast.error("Failed to download PDF");
    }
  };

  const renderGoalProgress = (goal) => {
    let progressText = "Not Yet Targeted";
    let progressColor = "text-gray-600 bg-gray-100";

    if (goal.achieved) {
      progressText = "Achieved";
      progressColor = "text-green-600 bg-green-100";
    } else if (goal.emergingMinimal) {
      progressText = "Emerging (Minimal Support)";
      progressColor = "text-blue-600 bg-blue-100";
    } else if (goal.emergingModerate) {
      progressText = "Emerging (Moderate Support)";
      progressColor = "text-yellow-600 bg-yellow-100";
    } else if (goal.emergingMaximum) {
      progressText = "Emerging (Maximum Support)";
      progressColor = "text-orange-600 bg-orange-100";
    }

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${progressColor}`}>
        {progressText}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[100] overflow-y-auto">
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              Progress Summary - {summary.clientName}
            </h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleDownloadPDF}
                className="inline-flex items-center px-3 py-2 bg-indigo-600 text-white text-sm font-medium rounded-sm hover:bg-indigo-700 transition-colors"
                title="Download PDF">
                <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                Download PDF
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Date</label>
              <div className="mt-1 text-sm text-gray-900">
                {new Date(summary.date).toLocaleDateString()}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Session Number</label>
              <div className="mt-1 text-sm text-gray-900">{summary.sessionNumber || 'N/A'}</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Grade</label>
              <div className="mt-1 text-sm text-gray-900">{summary.grade || 'N/A'}</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">School</label>
              <div className="mt-1 text-sm text-gray-900">{summary.school || 'N/A'}</div>
            </div>
          </div>

          {/* Communication Status */}
          {summary.currentCommunicationStatus && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Current Communication Status</label>
              <div className="mt-1 p-3 bg-gray-50 rounded-md text-sm text-gray-900">
                {summary.currentCommunicationStatus}
              </div>
            </div>
          )}

          {/* Goals */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Goals and Progress</label>
            <div className="space-y-3">
              {summary.goals?.map((goal, index) => (
                <div key={goal.id || index} className="p-4 border border-gray-200 rounded-md">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">{goal.description}</div>
                    </div>
                    <div className="ml-4">
                      {renderGoalProgress(goal)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Comments */}
          {summary.comments && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Comments</label>
              <div className="mt-1 p-3 bg-gray-50 rounded-md text-sm text-gray-900">
                {summary.comments}
              </div>
            </div>
          )}

          {/* Next Steps */}
          {summary.nextSteps && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Next Steps</label>
              <div className="mt-1 p-3 bg-gray-50 rounded-md text-sm text-gray-900">
                {summary.nextSteps}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProgressSummaryList;